import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useStore } from '../context/StoreContext.tsx';
import { BookOpen, PlusCircle, Filter, MoreHorizontal, X, Loader2, Sparkles } from 'lucide-react';
import { NarrativeSeason } from '../types.ts';

const NarrativeHub: React.FC = () => {
  const { narrativeSeasons, addNarrative } = useStore();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Mock data if store is empty for visualization, or use actual data if available
  const displaySeasons = narrativeSeasons.length > 0 ? narrativeSeasons : [];

  const handleCreateStory = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTitle.trim()) return;

    setIsSubmitting(true);
    try {
      const newSeason: NarrativeSeason = {
        id: `season-${Date.now()}`,
        title: newTitle,
        status: 'active',
        episodes: []
      };
      await addNarrative(newSeason);
      setIsModalOpen(false);
      setNewTitle('');
    } catch (error) {
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const columns = [
    { id: 'idea', label: '💡 Ideas', color: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200' },
    { id: 'scripted', label: '📝 In Dev', color: 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200' },
    { id: 'production', label: '🎬 Production', color: 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-200' },
    { id: 'published', label: '✅ Published', color: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200' }
  ];

  return (
    <div className="max-w-7xl mx-auto h-[calc(100vh-120px)] flex flex-col relative">
       <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[var(--text-primary)]">Narrative Hub</h1>
          <p className="text-[var(--text-secondary)]">Plan Chip's story arcs and content series.</p>
        </div>
        <div className="flex gap-2">
           <button className="px-3 py-2 border border-[var(--border-subtle)] rounded-lg text-sm font-medium text-[var(--text-secondary)] hover:bg-[var(--bg-secondary)] flex items-center gap-2 transition-colors">
             <Filter size={16} /> Filter
           </button>
           <button 
             onClick={() => setIsModalOpen(true)}
             className="px-4 py-2 bg-[var(--text-primary)] text-[var(--bg-primary)] rounded-lg text-sm font-medium flex items-center gap-2 hover:scale-105 transition-transform shadow-sm"
           >
             <PlusCircle size={16} /> New Story
           </button>
        </div>
      </div>

      <div className="flex-1 overflow-x-auto pb-6">
        {displaySeasons.length > 0 ? (
          displaySeasons.map(season => (
          <div key={season.id} className="mb-8 min-w-[1000px]">
             <div className="flex items-center gap-3 mb-4 px-1">
               <h2 className="text-lg font-bold text-[var(--text-primary)] flex items-center gap-2">
                 <BookOpen size={20} className="text-[var(--text-tertiary)]" />
                 {season.title}
               </h2>
               <span className="px-2 py-0.5 bg-[var(--bg-secondary)] text-[var(--text-secondary)] text-xs rounded-full font-medium">Season 1</span>
               <button className="ml-auto text-sm text-[var(--accent-primary)] font-medium hover:underline">+ Add Episode</button>
             </div>
             
             <div className="grid grid-cols-4 gap-4 h-full">
               {columns.map(col => (
                 <div key={col.id} className="bg-[var(--bg-secondary)] rounded-xl border border-[var(--border-subtle)] p-3 flex flex-col gap-3 min-h-[200px]">
                    <div className={`px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider w-fit ${col.color}`}>
                      {col.label}
                    </div>
                    
                    {season.episodes.filter(e => e.status === col.id || (col.id === 'production' && e.status === 'production')).map(episode => (
                      <div key={episode.id} className="bg-[var(--bg-primary)] p-4 rounded-lg border border-[var(--border-subtle)] shadow-sm hover:shadow-md transition-shadow cursor-pointer group">
                         <div className="flex justify-between items-start mb-2">
                            <span className="text-[10px] font-bold text-[var(--text-tertiary)] uppercase bg-[var(--bg-tertiary)] px-1.5 py-0.5 rounded">{episode.platform}</span>
                            <button className="text-[var(--text-tertiary)] hover:text-[var(--text-primary)] opacity-0 group-hover:opacity-100 transition-opacity"><MoreHorizontal size={14} /></button>
                         </div>
                         <h3 className="font-semibold text-[var(--text-primary)] text-sm mb-1">{episode.title}</h3>
                         <p className="text-xs text-[var(--text-secondary)] line-clamp-2 leading-relaxed">{episode.concept}</p>
                      </div>
                    ))}
                    
                    <button className="mt-auto py-2 text-xs text-[var(--text-tertiary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)] rounded-lg border border-transparent hover:border-dashed hover:border-[var(--border-default)] transition-all text-center">
                      + Add Card
                    </button>
                 </div>
               ))}
             </div>
          </div>
          ))
        ) : (
          <div className="h-full flex flex-col items-center justify-center border-2 border-dashed border-[var(--border-subtle)] rounded-xl bg-[var(--bg-secondary)] m-4">
            <div className="w-16 h-16 bg-[var(--bg-primary)] rounded-full flex items-center justify-center mb-4 border border-[var(--border-subtle)]">
                <BookOpen size={32} className="text-[var(--text-tertiary)]" />
            </div>
            <h3 className="text-lg font-medium text-[var(--text-primary)]">No stories yet</h3>
            <p className="text-[var(--text-secondary)] mb-6 text-sm">Create your first narrative arc to get started.</p>
            <button 
             onClick={() => setIsModalOpen(true)}
             className="px-6 py-2.5 bg-[var(--text-primary)] text-[var(--bg-primary)] rounded-lg text-sm font-medium hover:scale-105 transition-transform"
           >
             Create Story
           </button>
          </div>
        )}
      </div>

      {/* New Story Modal */}
      {isModalOpen && createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fadeIn">
          <div className="bg-[var(--bg-primary)] rounded-xl shadow-2xl w-full max-w-md border border-[var(--border-subtle)] animate-slideUp overflow-hidden">
             <div className="flex items-center justify-between p-5 border-b border-[var(--border-subtle)] bg-[var(--bg-secondary)]">
                <h3 className="font-bold text-lg text-[var(--text-primary)] flex items-center gap-2">
                    <Sparkles size={18} className="text-[var(--accent-primary)]" />
                    New Narrative Arc
                </h3>
                <button onClick={() => setIsModalOpen(false)} className="text-[var(--text-secondary)] hover:text-[var(--text-primary)] p-1 rounded-full hover:bg-[var(--bg-tertiary)] transition-colors">
                  <X size={20} />
                </button>
             </div>
             <form onSubmit={handleCreateStory} className="p-6 space-y-5">
                <div>
                  <label className="block text-sm font-bold text-[var(--text-primary)] mb-2">Story Title</label>
                  <input 
                    type="text" 
                    autoFocus
                    value={newTitle}
                    onChange={(e) => setNewTitle(e.target.value)}
                    placeholder="e.g. CES 2025 Survival Guide"
                    className="input-field"
                    required
                  />
                </div>
                <div>
                   <label className="block text-sm font-bold text-[var(--text-primary)] mb-2">Initial Season</label>
                   <input type="text" value="Season 1" disabled className="input-field opacity-60 bg-[var(--bg-tertiary)]" />
                </div>
                
                <div className="pt-2">
                    <div className="p-3 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 rounded-lg text-xs border border-blue-100 dark:border-blue-800">
                        This creates a new Kanban board for tracking episodes and content ideas for this specific storyline.
                    </div>
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t border-[var(--border-subtle)]">
                   <button 
                     type="button" 
                     onClick={() => setIsModalOpen(false)}
                     className="px-4 py-2 text-sm font-medium text-[var(--text-secondary)] hover:bg-[var(--bg-secondary)] rounded-lg transition-colors"
                   >
                     Cancel
                   </button>
                   <button 
                     type="submit" 
                     disabled={isSubmitting || !newTitle.trim()}
                     className="btn-primary flex items-center gap-2"
                   >
                     {isSubmitting ? <Loader2 className="animate-spin" size={16} /> : <PlusCircle size={16} />}
                     Create Story
                   </button>
                </div>
             </form>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default NarrativeHub;